import {
  RepairRequest,
  RepairStatus,
  RepairWorkflowNextStep,
  RepairWorkflowV15ReadModel,
  RepairWorkflowV15Stage,
  RepairWorkflowVersion,
  type FinalRepairQuote,
  type PreliminaryEstimate,
  type RepairHandoverEvent,
} from '../types/firestore';
import { FulfillmentMode, resolveRepairRequestFulfillmentSnapshot } from '../shared/fulfillment';

export type RepairWorkflowReadActorContext = 'requester' | 'service' | 'admin';

type RepairWorkflowReadableRequest = Partial<RepairRequest>;

const STAGE_LABELS: Record<RepairWorkflowV15Stage, string> = {
  legacy_request_created: 'Zgłoszenie utworzone',
  legacy_quote_preparation: 'Wycena legacy',
  legacy_quote_decision: 'Decyzja o wycenie legacy',
  legacy_repair_ready: 'Gotowe do realizacji legacy',
  legacy_repair_in_progress: 'Naprawa legacy',
  legacy_payment_pending: 'Rozliczenie legacy',
  preliminary_estimate_pending: 'Wycena wstępna',
  preliminary_estimate_decision: 'Decyzja o wycenie wstępnej',
  awaiting_customer_delivery: 'Oczekiwanie na dostarczenie',
  awaiting_service_pickup: 'Oczekiwanie na odbiór',
  awaiting_onsite_visit: 'Oczekiwanie na wizytę',
  awaiting_fulfillment_confirmation: 'Oczekiwanie na potwierdzenie realizacji',
  diagnosis_in_progress: 'Diagnoza',
  final_quote_decision: 'Decyzja o finalnej wycenie',
  repair_ready: 'Gotowe do naprawy',
  repair_in_progress: 'Naprawa',
  cash_settlement_pending: 'Rozliczenie gotówkowe',
  completed: 'Zamknięte',
  cancelled: 'Anulowane',
  rejected: 'Odrzucone',
  degraded: 'Wymaga uzupełnienia danych',
};

const NEXT_STEPS: Record<string, RepairWorkflowNextStep> = {
  legacy_none: {
    actor: 'system',
    kind: 'informational',
    id: 'legacy_none',
    label: 'Obecny workflow legacy pozostaje bez zmian.',
  },
  legacy_accept_quote: {
    actor: 'requester',
    kind: 'action',
    id: 'legacy_accept_quote',
    label: 'Zaakceptuj albo odrzuć wycenę.',
  },
  legacy_continue_repair: {
    actor: 'service',
    kind: 'action',
    id: 'legacy_continue_repair',
    label: 'Kontynuuj realizację naprawy.',
  },
  legacy_settlement: {
    actor: 'system',
    kind: 'informational',
    id: 'legacy_settlement',
    label: 'Rozliczenie wymaga koordynacji właściciela zgłoszenia i serwisu.',
  },
  submit_preliminary_estimate: {
    actor: 'service',
    kind: 'action',
    id: 'submit_preliminary_estimate',
    label: 'Przygotuj wycenę wstępną.',
  },
  decide_preliminary_estimate: {
    actor: 'requester',
    kind: 'action',
    id: 'decide_preliminary_estimate',
    label: 'Zaakceptuj albo odrzuć wycenę wstępną.',
  },
  await_customer_delivery: {
    actor: 'requester',
    kind: 'action',
    id: 'await_customer_delivery',
    label: 'Dostarcz sprzęt do serwisu.',
  },
  await_service_pickup: {
    actor: 'service',
    kind: 'action',
    id: 'await_service_pickup',
    label: 'Odbierz sprzęt od właściciela zgłoszenia.',
  },
  await_onsite_visit: {
    actor: 'service',
    kind: 'action',
    id: 'await_onsite_visit',
    label: 'Zrealizuj umówioną wizytę.',
  },
  submit_final_quote: {
    actor: 'service',
    kind: 'action',
    id: 'submit_final_quote',
    label: 'Zakończ diagnozę i przygotuj finalną wycenę.',
  },
  decide_final_quote: {
    actor: 'requester',
    kind: 'action',
    id: 'decide_final_quote',
    label: 'Zaakceptuj albo odrzuć finalną wycenę.',
  },
  continue_repair: {
    actor: 'service',
    kind: 'action',
    id: 'continue_repair',
    label: 'Rozpocznij albo kontynuuj naprawę.',
  },
  start_repair_work: {
    actor: 'service',
    kind: 'action',
    id: 'start_repair_work',
    label: 'Rozpocznij naprawę.',
  },
  complete_repair_work: {
    actor: 'service',
    kind: 'action',
    id: 'complete_repair_work',
    label: 'Oznacz naprawę jako wykonaną.',
  },
  prepare_cash_settlement: {
    actor: 'service',
    kind: 'action',
    id: 'prepare_cash_settlement',
    label: 'Przygotuj rozliczenie gotówką lub bezpośrednio.',
  },
  confirm_cash_release: {
    actor: 'service',
    kind: 'action',
    id: 'confirm_cash_release',
    label: 'Potwierdź rozliczenie i zakończ zlecenie.',
  },
  closed: {
    actor: 'system',
    kind: 'terminal',
    id: 'closed',
    label: 'Sprawa jest zamknięta.',
  },
  degraded: {
    actor: 'system',
    kind: 'degraded',
    id: 'degraded',
    label: 'Dane workflow są niepełne; nie inferuj decyzji ani postępu.',
  },
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value));

const hasValue = (value: unknown): boolean => value !== undefined && value !== null;

const hasPreliminaryCreatedAt = (value: unknown): boolean =>
  isRecord(value) && hasValue(value.createdAt);

const hasFinalQuoteCreatedAt = (value: unknown): boolean =>
  isRecord(value) && hasValue(value.createdAt);

const hasAcceptedAt = (value: unknown): boolean => isRecord(value) && hasValue(value.acceptedAt);

const hasRejectedAt = (value: unknown): boolean => isRecord(value) && hasValue(value.rejectedAt);

const isKnownHandoverEvent = (value: unknown): value is RepairHandoverEvent =>
  isRecord(value) &&
  (value.type === 'customerDropoffReceived' ||
    value.type === 'servicePickupCompleted' ||
    value.type === 'onsiteVisitStarted' ||
    value.type === 'onsiteVisitCompleted' ||
    value.type === 'releaseConfirmed');

const getStageLabel = (stage: RepairWorkflowV15Stage): string => STAGE_LABELS[stage];

const buildReadModel = (
  version: RepairWorkflowVersion,
  stage: RepairWorkflowV15Stage,
  nextStep: RepairWorkflowNextStep,
  issues: string[] = []
): RepairWorkflowV15ReadModel => ({
  version,
  isLegacy: version === 1,
  isV15: version === 2,
  stage,
  stageLabel: getStageLabel(stage),
  nextStep,
  issues,
});

export const resolveRepairWorkflowVersion = (
  request?: RepairWorkflowReadableRequest | null
): RepairWorkflowVersion => (request?.workflowVersion === 2 ? 2 : 1);

export const isLegacyFlatQuoteRequest = (request?: RepairWorkflowReadableRequest | null): boolean =>
  resolveRepairWorkflowVersion(request) === 1 &&
  (typeof request?.quotedPrice === 'number' ||
    typeof request?.quoteDescription === 'string' ||
    hasValue(request?.quotedAt));

export const isWorkflowV15Request = (request?: RepairWorkflowReadableRequest | null): boolean =>
  resolveRepairWorkflowVersion(request) === 2;

const resolveLegacyStage = (request: RepairWorkflowReadableRequest): RepairWorkflowV15ReadModel => {
  switch (request.status) {
    case RepairStatus.COMPLETED:
      return buildReadModel(1, 'completed', NEXT_STEPS.closed);
    case RepairStatus.CANCELLED_BY_CLIENT:
    case RepairStatus.CANCELLED_BY_SERVICE:
      return buildReadModel(1, 'cancelled', NEXT_STEPS.closed);
    case RepairStatus.REJECTED:
      return buildReadModel(1, 'rejected', NEXT_STEPS.closed);
    case RepairStatus.PENDING_QUOTE:
      return buildReadModel(1, 'legacy_quote_preparation', NEXT_STEPS.legacy_none);
    case RepairStatus.QUOTED:
      return buildReadModel(1, 'legacy_quote_decision', NEXT_STEPS.legacy_accept_quote);
    case RepairStatus.ACCEPTED:
      return buildReadModel(1, 'legacy_repair_ready', NEXT_STEPS.legacy_continue_repair);
    case RepairStatus.IN_PROGRESS:
      return buildReadModel(1, 'legacy_repair_in_progress', NEXT_STEPS.legacy_continue_repair);
    case RepairStatus.PENDING_PAYMENT:
      return buildReadModel(1, 'legacy_payment_pending', NEXT_STEPS.legacy_settlement);
    case RepairStatus.NEW:
    default:
      return buildReadModel(1, 'legacy_request_created', NEXT_STEPS.legacy_none);
  }
};

const getIntakeEventTypeForMode = (mode: FulfillmentMode): RepairHandoverEvent['type'] => {
  if (mode === 'servicePickup') return 'servicePickupCompleted';
  if (mode === 'onsiteService') return 'onsiteVisitStarted';
  return 'customerDropoffReceived';
};

const getAwaitingStageForMode = (mode: FulfillmentMode): RepairWorkflowV15Stage => {
  if (mode === 'servicePickup') return 'awaiting_service_pickup';
  if (mode === 'onsiteService') return 'awaiting_onsite_visit';
  return 'awaiting_customer_delivery';
};

const getAwaitingNextStepForMode = (mode: FulfillmentMode): RepairWorkflowNextStep => {
  if (mode === 'servicePickup') return NEXT_STEPS.await_service_pickup;
  if (mode === 'onsiteService') return NEXT_STEPS.await_onsite_visit;
  return NEXT_STEPS.await_customer_delivery;
};

const hasIntakeEvent = (
  events: RepairWorkflowReadableRequest['handoverEvents'],
  mode: FulfillmentMode
): boolean =>
  Array.isArray(events) &&
  events.some((event) => {
    if (!isKnownHandoverEvent(event)) {
      return false;
    }
    if (event.type === getIntakeEventTypeForMode(mode)) {
      return true;
    }
    return mode === 'onsiteService' && event.type === 'onsiteVisitCompleted';
  });

const hasReleaseEvent = (events: RepairWorkflowReadableRequest['handoverEvents']): boolean =>
  Array.isArray(events) &&
  events.some((event) => isKnownHandoverEvent(event) && event.type === 'releaseConfirmed');

const hasCashSettlement = (request: RepairWorkflowReadableRequest): boolean =>
  isRecord(request.cashSettlement) &&
  (request.cashSettlement.method === 'cash' ||
    request.cashSettlement.method === 'cash_or_direct') &&
  (request.cashSettlement.status === 'confirmed' || hasValue(request.cashSettlement.confirmedAt));

const isPartialDecisionObject = (value: unknown): boolean =>
  isRecord(value) && !hasFinalQuoteCreatedAt(value);

const resolveV15Stage = (request: RepairWorkflowReadableRequest): RepairWorkflowV15ReadModel => {
  switch (request.status) {
    case RepairStatus.COMPLETED:
      return buildReadModel(2, 'completed', NEXT_STEPS.closed);
    case RepairStatus.CANCELLED_BY_CLIENT:
    case RepairStatus.CANCELLED_BY_SERVICE:
      return buildReadModel(2, 'cancelled', NEXT_STEPS.closed);
    case RepairStatus.PRELIMINARY_REJECTED:
    case RepairStatus.FINAL_QUOTE_REJECTED:
    case RepairStatus.REJECTED:
      return buildReadModel(2, 'rejected', NEXT_STEPS.closed);
    default:
      break;
  }

  const preliminaryEstimate = request.preliminaryEstimate as PreliminaryEstimate | undefined;
  if (request.status === RepairStatus.DIAGNOSIS_IN_PROGRESS) {
    return buildReadModel(2, 'diagnosis_in_progress', NEXT_STEPS.submit_final_quote);
  }

  if (!preliminaryEstimate) {
    return buildReadModel(
      2,
      'preliminary_estimate_pending',
      NEXT_STEPS.submit_preliminary_estimate
    );
  }
  if (!hasPreliminaryCreatedAt(preliminaryEstimate)) {
    return buildReadModel(2, 'degraded', NEXT_STEPS.degraded, ['partial-preliminaryEstimate']);
  }
  if (hasRejectedAt(preliminaryEstimate)) {
    return buildReadModel(2, 'rejected', NEXT_STEPS.closed);
  }
  if (!hasAcceptedAt(preliminaryEstimate)) {
    return buildReadModel(
      2,
      'preliminary_estimate_decision',
      NEXT_STEPS.decide_preliminary_estimate
    );
  }

  const fulfillment = resolveRepairRequestFulfillmentSnapshot(request);
  if (!fulfillment.selectedMode) {
    return buildReadModel(2, 'degraded', NEXT_STEPS.degraded, [
      ...fulfillment.issues,
      'missing-v15-fulfillment-mode',
    ]);
  }

  if (!hasIntakeEvent(request.handoverEvents, fulfillment.selectedMode)) {
    return buildReadModel(
      2,
      getAwaitingStageForMode(fulfillment.selectedMode),
      getAwaitingNextStepForMode(fulfillment.selectedMode),
      fulfillment.issues
    );
  }

  const finalQuote = request.finalQuote as FinalRepairQuote | undefined;
  if (!finalQuote) {
    return buildReadModel(
      2,
      'diagnosis_in_progress',
      NEXT_STEPS.submit_final_quote,
      fulfillment.issues
    );
  }
  if (isPartialDecisionObject(finalQuote)) {
    return buildReadModel(2, 'degraded', NEXT_STEPS.degraded, ['partial-finalQuote']);
  }
  if (finalQuote.status === 'rejected') {
    return buildReadModel(2, 'rejected', NEXT_STEPS.closed);
  }
  if (finalQuote.status !== 'accepted' && request.status !== RepairStatus.FINAL_QUOTE_ACCEPTED) {
    return buildReadModel(2, 'final_quote_decision', NEXT_STEPS.decide_final_quote);
  }

  if (request.status === RepairStatus.FINAL_QUOTE_ACCEPTED) {
    return buildReadModel(2, 'repair_ready', NEXT_STEPS.start_repair_work, fulfillment.issues);
  }

  if (request.status === RepairStatus.REPAIR_IN_PROGRESS) {
    return buildReadModel(
      2,
      'repair_in_progress',
      NEXT_STEPS.complete_repair_work,
      fulfillment.issues
    );
  }

  if (request.status === RepairStatus.REPAIR_COMPLETED) {
    return buildReadModel(
      2,
      'cash_settlement_pending',
      NEXT_STEPS.prepare_cash_settlement,
      fulfillment.issues
    );
  }

  if (request.status === RepairStatus.AWAITING_CASH_SETTLEMENT) {
    const settlementIssues = [
      ...fulfillment.issues,
      ...(hasCashSettlement(request) ? [] : ['missing-cashSettlement-confirmation']),
    ];
    return buildReadModel(
      2,
      'cash_settlement_pending',
      NEXT_STEPS.confirm_cash_release,
      settlementIssues
    );
  }

  if (request.status === RepairStatus.PENDING_PAYMENT) {
    const settlementIssues = [
      ...fulfillment.issues,
      ...(hasCashSettlement(request) ? [] : ['missing-cashSettlement-confirmation']),
      ...(hasReleaseEvent(request.handoverEvents) ? [] : ['missing-release-confirmation']),
    ];
    return buildReadModel(
      2,
      'cash_settlement_pending',
      NEXT_STEPS.confirm_cash_release,
      settlementIssues
    );
  }

  if (request.status === RepairStatus.IN_PROGRESS) {
    return buildReadModel(2, 'repair_in_progress', NEXT_STEPS.continue_repair, fulfillment.issues);
  }

  return buildReadModel(2, 'repair_ready', NEXT_STEPS.continue_repair, fulfillment.issues);
};

export const resolveRepairWorkflowStage = (
  request?: RepairWorkflowReadableRequest | null
): RepairWorkflowV15Stage => resolveRepairWorkflowReadModel(request).stage;

export const resolveRepairWorkflowNextStep = (
  request?: RepairWorkflowReadableRequest | null,
  _actorContext?: RepairWorkflowReadActorContext
): RepairWorkflowNextStep => resolveRepairWorkflowReadModel(request).nextStep;

export const resolveRepairWorkflowReadModel = (
  request?: RepairWorkflowReadableRequest | null
): RepairWorkflowV15ReadModel => {
  if (!request) {
    return buildReadModel(1, 'degraded', NEXT_STEPS.degraded, ['missing-request']);
  }

  const version = resolveRepairWorkflowVersion(request);
  return version === 2 ? resolveV15Stage(request) : resolveLegacyStage(request);
};

export const getRepairWorkflowV15FulfillmentMessage = (
  request?: RepairWorkflowReadableRequest | null
): string => {
  const fulfillment = resolveRepairRequestFulfillmentSnapshot(request || {});

  if (fulfillment.selectedMode === 'customerDropoff') {
    return 'Oczekujemy na dostarczenie sprzętu do serwisu.';
  }

  if (fulfillment.selectedMode === 'servicePickup') {
    return 'Serwis powinien odebrać sprzęt pod wskazanym adresem.';
  }

  if (fulfillment.selectedMode === 'onsiteService') {
    return 'Oczekiwanie na wizytę serwisu u klienta.';
  }

  return 'Sposób realizacji nie jest kompletny. Nie można bezpiecznie wskazać kolejnego kroku.';
};

export const getRepairWorkflowV15StageDescription = (
  request?: RepairWorkflowReadableRequest | null
): string | null => {
  const readModel = resolveRepairWorkflowReadModel(request);

  if (!readModel.isV15) {
    return null;
  }

  if (
    readModel.stage === 'awaiting_customer_delivery' ||
    readModel.stage === 'awaiting_service_pickup' ||
    readModel.stage === 'awaiting_onsite_visit' ||
    readModel.stage === 'awaiting_fulfillment_confirmation'
  ) {
    return getRepairWorkflowV15FulfillmentMessage(request);
  }

  if (readModel.stage === 'rejected') {
    return 'Wycena wstępna została odrzucona.';
  }

  if (readModel.stage === 'preliminary_estimate_pending') {
    return 'Sprawa czeka na wycenę wstępną serwisu.';
  }

  if (readModel.stage === 'preliminary_estimate_decision') {
    return 'Wycena wstępna czeka na decyzję właściciela zgłoszenia.';
  }

  if (readModel.stage === 'diagnosis_in_progress') {
    return 'Serwis potwierdził przekazanie i prowadzi diagnozę. Po diagnozie serwis zapisze finalną wycenę.';
  }

  if (readModel.stage === 'final_quote_decision') {
    return 'Diagnoza i finalna wycena czekają na decyzję właściciela zgłoszenia.';
  }

  if (readModel.stage === 'repair_ready' && request?.status === RepairStatus.FINAL_QUOTE_ACCEPTED) {
    return 'Finalna wycena została zaakceptowana. Serwis może rozpocząć naprawę.';
  }

  if (request?.status === RepairStatus.REPAIR_IN_PROGRESS) {
    return 'Serwis prowadzi naprawę zgodnie z zaakceptowaną finalną wyceną.';
  }

  if (request?.status === RepairStatus.REPAIR_COMPLETED) {
    return 'Naprawa została wykonana. Serwis powinien przygotować rozliczenie gotówką lub bezpośrednio.';
  }

  if (request?.status === RepairStatus.AWAITING_CASH_SETTLEMENT) {
    return 'Naprawa czeka na rozliczenie gotówką lub bezpośrednio i potwierdzenie zakończenia.';
  }

  if (readModel.stage === 'degraded') {
    return 'Dane workflow są niepełne. Backend pozostaje źródłem prawdy dla kolejnego kroku.';
  }

  return readModel.nextStep.label;
};
